import React, { useState, useRef } from 'react';
import gateApi from '../api';
import {
  C, FormShell, Field, TextInput, Select, TextArea, SubmitButton, Banner,
} from './FormKit';

const VEHICLE_TYPES = ['22WHL', '18WHL', '16WHL', '14WHL', '12WHL', '10WHL', '06WHL', 'TRACTOR', 'Others'];
const LOAD_TYPES = ['10mm', '20mm', '30mm', '40mm', '60mm', 'DUST', 'BOULDER', 'WMM', 'GSB', 'BC-1-Dust', 'OTHER'];

export default function EntryForm() {
  const [plate, setPlate]   = useState('');
  const [vType, setVType]   = useState('');
  const [load, setLoad]     = useState('');
  const [notes, setNotes]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null); // { ok, msg }
  const plateRef = useRef(null);

  function reset() {
    setPlate(''); setVType(''); setLoad(''); setNotes('');
    setResult(null);
  }

  async function submit() {
    const clean = plate.toUpperCase().trim();
    if (!clean) {
      setResult({ ok: false, msg: 'Enter the number plate first.' });
      plateRef.current?.querySelector('input')?.focus();
      return;
    }
    if (!vType) { setResult({ ok: false, msg: 'Select the vehicle type.' }); return; }
    if (!load)  { setResult({ ok: false, msg: 'Select the expected load.' }); return; }

    setBusy(true); setResult(null);
    try {
      const r = await gateApi.directEntry({
        licensePlate: clean, vehicleType: vType, expectedLoad: load, operatorNotes: notes,
      });
      const t = r.entryTime ? new Date(r.entryTime).toLocaleTimeString() : '';
      setResult({ ok: true, msg: `${r.plate} logged IN at ${t}. Ready for next vehicle.` });
      setPlate(''); setVType(''); setLoad(''); setNotes('');
      setTimeout(() => plateRef.current?.querySelector('input')?.focus(), 50);
    } catch (e) {
      const data = e.response?.data;
      if (data?.error === 'DUPLICATE') {
        setResult({ ok: false, msg: `${clean} is already inside — it has an open trip.` });
      } else {
        setResult({ ok: false, msg: data?.message || data?.error || 'Could not save. Check connection.' });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormShell title="Entry" accent={C.green}>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>Vehicle entry</h1>
      <p style={{ color: C.muted, fontSize: 14, margin: '0 0 26px' }}>
        Log a vehicle into the site. This starts the time-inside clock.
      </p>

      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      <div ref={plateRef}>
        <Field label="Number plate" hint="Read it directly off the vehicle.">
          <TextInput value={plate} onChange={setPlate}
            placeholder="e.g. BR01AB1234" mono autoFocus
            style={{ fontSize: 22, fontWeight: 700, textTransform: 'uppercase' }} />
        </Field>
      </div>

      <Field label="Vehicle type">
        <Select value={vType} onChange={setVType} options={VEHICLE_TYPES}
          placeholder="Select vehicle type…" />
      </Field>

      <Field label="Expected load">
        <Select value={load} onChange={setLoad} options={LOAD_TYPES}
          placeholder="Select expected load…" />
      </Field>

      <Field label="Notes (optional)">
        <TextArea value={notes} onChange={setNotes} placeholder="Transporter, driver, remarks…" />
      </Field>

      <div style={{ marginTop: 8 }}>
        <SubmitButton onClick={submit} disabled={busy} color={C.green}>
          {busy ? 'Saving…' : 'Log entry'}
        </SubmitButton>
      </div>

      <button onClick={reset} style={{
        marginTop: 14, width: '100%', background: 'none', border: 'none',
        color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer',
        letterSpacing: '0.08em',
      }}>CLEAR FORM</button>
    </FormShell>
  );
}